import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LanguageCode, LanguageProgress, WordProgress, WordMasteryLevel } from '../types/language';
import { todayStr } from '../utils/date';
import { useCompetitionStore } from './competitionStore';
import { useProgressStore } from './progressStore';
import { nextDailyReview } from '../utils/srs';

// Schedule the next review date for a Leitner-style mastery level (0..5).
// Delegates to the shared SRS utility so flashcardStore + languageStore stay
// in lockstep on tuning.
function nextReviewDate(level: WordMasteryLevel, today: string): string {
  return nextDailyReview(level, today);
}

/**
 * Backfills progressStore with one completion per (child, language, date)
 * inferred from existing languageStore data — `wordProgress[].lastPracticed`,
 * per-language `lastPracticeDate`, and `checkpointsPassed` dates.
 *
 * Idempotent: each completion uses a deterministic activityId of
 * `language:{lang}:session:{date}`, so repeat runs don't double-write.
 *
 * Needed because `recordPracticeSession` only began mirroring into
 * progressStore in a recent change, leaving historical language practice
 * invisible to the parent dashboard's coverage wheel and gap alerts.
 */
export function backfillLanguageCompletions(): void {
  const langState = useLanguageStore.getState();
  const progressState = useProgressStore.getState();

  const existingIds = new Set(progressState.completions.map(c => c.activityId));

  for (const [childId, byLang] of Object.entries(langState.progress)) {
    for (const [lang, lp] of Object.entries(byLang)) {
      const dates = new Set<string>();
      if (lp.lastPracticeDate) dates.add(lp.lastPracticeDate);
      for (const wp of Object.values(lp.wordProgress ?? {})) {
        if (wp.lastPracticed) dates.add(wp.lastPracticed);
      }
      for (const date of Object.values(lp.checkpointsPassed ?? {})) {
        if (date) dates.add(date);
      }

      for (const date of dates) {
        const activityId = `language:${lang}:session:${date}`;
        if (existingIds.has(activityId)) continue;
        progressState.addCompletion({
          childId,
          activityId,
          scheduleId: `language:${lang}`,
          date,
          subject: 'language',
          durationMinutes: 10,
          starsEarned: 2,
          xpEarned: 0,
          themeId: 'language',
        });
        existingIds.add(activityId);
      }
    }
  }
}

function emptyProgress(langCode: LanguageCode): LanguageProgress {
  return {
    languageCode: langCode,
    totalWordsIntroduced: 0,
    totalWordsLearned: 0,
    totalWordsMastered: 0,
    wordProgress: {},
    streakDays: 0,
    lastPracticeDate: '',
    checkpointsPassed: {},
  };
}

interface LanguageState {
  progress: Record<string, Record<string, LanguageProgress>>; // childId -> langCode -> progress

  recordWordAttempt: (childId: string, lang: LanguageCode, wordId: string, correct: boolean) => void;
  introduceWord: (childId: string, lang: LanguageCode, wordId: string) => void;
  getWordProgress: (childId: string, lang: LanguageCode, wordId: string) => WordProgress | null;
  getLanguageProgress: (childId: string, lang: LanguageCode) => LanguageProgress;
  getWordsForReview: (childId: string, lang: LanguageCode) => string[];
  recordPracticeSession: (childId: string, lang: LanguageCode) => void;
  markCheckpointPassed: (childId: string, lang: LanguageCode, stage: string) => void;
}

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      progress: {},

      introduceWord: (childId, lang, wordId) => {
        set(state => {
          const childProgress = { ...(state.progress[childId] || {}) };
          const langProgress = { ...(childProgress[lang] || emptyProgress(lang)) };
          const wp = langProgress.wordProgress[wordId];

          if (!wp) {
            const today = todayStr();
            langProgress.wordProgress = {
              ...langProgress.wordProgress,
              [wordId]: {
                wordId,
                level: 1 as WordMasteryLevel,
                correctCount: 0,
                incorrectCount: 0,
                lastPracticed: today,
                nextReviewDate: nextReviewDate(1, today),
                streak: 0,
              },
            };
            langProgress.totalWordsIntroduced++;
          }

          childProgress[lang] = langProgress;
          return { progress: { ...state.progress, [childId]: childProgress } };
        });
      },

      recordWordAttempt: (childId, lang, wordId, correct) => {
        set(state => {
          const childProgress = { ...(state.progress[childId] || {}) };
          const langProgress = { ...(childProgress[lang] || emptyProgress(lang)) };
          const today = todayStr();

          const existing = langProgress.wordProgress[wordId];
          const wp: WordProgress = existing
            ? { ...existing }
            : {
                wordId,
                level: 0 as WordMasteryLevel,
                correctCount: 0,
                incorrectCount: 0,
                lastPracticed: today,
                nextReviewDate: today,
                streak: 0,
              };

          const wasLearned = wp.level >= 3;
          const wasMastered = wp.level >= 5;

          if (correct) {
            wp.correctCount++;
            wp.streak++;
            // Level up: need consecutive correct answers matching the level threshold
            if (wp.streak >= wp.level + 1 && wp.level < 5) {
              wp.level = Math.min(5, wp.level + 1) as WordMasteryLevel;
            }
          } else {
            wp.incorrectCount++;
            wp.streak = 0;
            // Drop a level on miss, but not below 1 (introduced)
            if (wp.level > 1) {
              wp.level = (wp.level - 1) as WordMasteryLevel;
            }
          }

          wp.lastPracticed = today;
          wp.nextReviewDate = nextReviewDate(wp.level, today);

          langProgress.wordProgress = { ...langProgress.wordProgress, [wordId]: wp };

          // Update counts
          const isNowLearned = wp.level >= 3;
          const isNowMastered = wp.level >= 5;
          if (!wasLearned && isNowLearned) langProgress.totalWordsLearned++;
          if (wasLearned && !isNowLearned) langProgress.totalWordsLearned = Math.max(0, langProgress.totalWordsLearned - 1);
          if (!wasMastered && isNowMastered) langProgress.totalWordsMastered++;
          if (wasMastered && !isNowMastered) langProgress.totalWordsMastered = Math.max(0, langProgress.totalWordsMastered - 1);

          childProgress[lang] = langProgress;
          return { progress: { ...state.progress, [childId]: childProgress } };
        });
      },

      getWordProgress: (childId, lang, wordId) => {
        const state = get();
        return state.progress[childId]?.[lang]?.wordProgress[wordId] ?? null;
      },

      getLanguageProgress: (childId, lang) => {
        const state = get();
        return state.progress[childId]?.[lang] ?? emptyProgress(lang);
      },

      getWordsForReview: (childId, lang) => {
        const state = get();
        const langProgress = state.progress[childId]?.[lang];
        if (!langProgress) return [];

        const today = todayStr();
        return Object.values(langProgress.wordProgress)
          .filter(wp => wp.level >= 1 && wp.nextReviewDate <= today)
          .sort((a, b) => a.level - b.level) // prioritize lower mastery
          .map(wp => wp.wordId);
      },

      markCheckpointPassed: (childId, lang, stage) => {
        set(state => {
          const childProgress = { ...(state.progress[childId] || {}) };
          const langProgress = { ...(childProgress[lang] || emptyProgress(lang)) };
          const passed = { ...(langProgress.checkpointsPassed || {}) };
          if (passed[stage]) return state; // already passed — idempotent
          passed[stage] = todayStr();
          langProgress.checkpointsPassed = passed;
          childProgress[lang] = langProgress;
          return { progress: { ...state.progress, [childId]: childProgress } };
        });
      },

      recordPracticeSession: (childId, lang) => {
        const today = todayStr();
        const isFirstToday = get().progress[childId]?.[lang]?.lastPracticeDate !== today;

        set(state => {
          const childProgress = { ...(state.progress[childId] || {}) };
          const langProgress = { ...(childProgress[lang] || emptyProgress(lang)) };

          if (langProgress.lastPracticeDate === today) return state; // already recorded

          // Check if streak continues
          const yesterday = new Date(today + 'T00:00:00');
          yesterday.setDate(yesterday.getDate() - 1);
          const yesterdayStr = yesterday.toISOString().split('T')[0];

          if (langProgress.lastPracticeDate === yesterdayStr) {
            langProgress.streakDays++;
          } else if (langProgress.lastPracticeDate !== today) {
            langProgress.streakDays = 1;
          }

          langProgress.lastPracticeDate = today;
          childProgress[lang] = langProgress;
          return { progress: { ...state.progress, [childId]: childProgress } };
        });

        // Mirror into progressStore so the parent dashboard's subject coverage,
        // gap alerts, weekly digest, and streaks see foreign-language practice.
        // One completion per day per language — matches the streak cadence above.
        if (isFirstToday) {
          useProgressStore.getState().addCompletion({
            childId,
            activityId: `language:${lang}:session:${today}`,
            scheduleId: `language:${lang}`,
            date: today,
            subject: 'language',
            durationMinutes: 10,
            starsEarned: 2,
            xpEarned: 0,
            themeId: 'language',
          });
        }

        // Submit total words learned (level ≥ 3) across all languages to competition
        const allLangProgress = Object.values(get().progress[childId] || {});
        const totalLearned = allLangProgress.reduce((sum, lp) => {
          return sum + Object.values(lp.wordProgress).filter(wp => wp.level >= 3).length;
        }, 0);
        if (totalLearned > 0) {
          const comp = useCompetitionStore.getState();
          for (const evt of comp.getActiveEventsForQuiz('language-words')) {
            const existing = comp.results.filter(r => r.eventId === evt.id && r.childId === childId);
            const best = existing.reduce((m, r) => Math.max(m, r.score), -1);
            if (totalLearned > best) comp.submitResult(evt.id, childId, totalLearned, []);
          }
        }
      },
    }),
    {
      name: 'homeschool-language',
      version: 1,
      // v0 → v1: stage progression switched from "mastered word count" thresholds
      // to explicit checkpoint quiz passes. Grandfather existing users by
      // auto-passing checkpoints whose old word-count threshold was already met,
      // so they aren't suddenly demoted to numbers-basic and forced to redo
      // checkpoints they never had a chance to take.
      migrate: (state: any, version: number) => {
        if (version >= 1) return state;
        const LEGACY_NEXT_THRESHOLDS: Record<string, number> = {
          'numbers-basic': 5,
          'colors': 12,
          'greetings': 18,
          'travel-essentials': 25,
          'theme-current': 35,
          'numbers-advanced': 45,
          'theme-review': 60,
        };
        const today = todayStr();
        const progress = state?.progress ?? {};
        for (const childId of Object.keys(progress)) {
          for (const lang of Object.keys(progress[childId])) {
            const lp = progress[childId][lang];
            if (!lp.checkpointsPassed) lp.checkpointsPassed = {};
            const masteredCount = Object.values(lp.wordProgress || {})
              .filter((wp: any) => wp.level >= 3).length;
            for (const [stage, threshold] of Object.entries(LEGACY_NEXT_THRESHOLDS)) {
              if (masteredCount >= threshold && !lp.checkpointsPassed[stage]) {
                lp.checkpointsPassed[stage] = today;
              }
            }
          }
        }
        return state;
      },
    }
  )
);
